import { Howl } from "howler";
import type { IAudioEngine } from "../types";

/**
 * Howler.js implementation of IAudioEngine.
 * Per Software - Audio Standard: this is the ONLY file that imports howler.
 * All game code depends on IAudioEngine, never on Howl directly.
 */
export class HowlerAudioEngine implements IAudioEngine {
  private music = new Map<string, Howl>();
  private sfx = new Map<string, Howl>();
  private currentMusicId: string | null = null;

  private masterVolume = 0.8;
  private musicVolume = 0.7;
  private sfxVolume = 1.0;

  loadMusic(id: string, src: string): void {
    if (this.music.has(id)) return;
    this.music.set(id, new Howl({ src: [src], loop: true, volume: this.effectiveMusicVolume() }));
  }

  loadSfx(id: string, src: string): void {
    if (this.sfx.has(id)) return;
    this.sfx.set(id, new Howl({ src: [src], volume: this.effectiveSfxVolume() }));
  }

  playMusic(id: string, opts?: { fadeInMs?: number; loop?: boolean }): void {
    const track = this.music.get(id);
    if (!track) return;
    if (this.currentMusicId && this.currentMusicId !== id) {
      const prevId = this.currentMusicId;
      const prev = this.music.get(prevId);
      if (prev) {
        prev.off("fade");
        prev.fade(prev.volume(), 0, opts?.fadeInMs ?? 500);
        prev.once("fade", () => {
          if (this.currentMusicId !== prevId) prev.stop();
        });
      }
    }
    track.loop(opts?.loop ?? true);
    track.volume(0);
    track.play();
    track.fade(0, this.effectiveMusicVolume(), opts?.fadeInMs ?? 500);
    this.currentMusicId = id;
  }

  stopMusic(opts?: { fadeOutMs?: number }): void {
    if (!this.currentMusicId) return;
    const track = this.music.get(this.currentMusicId);
    if (!track) return;
    track.fade(track.volume(), 0, opts?.fadeOutMs ?? 500);
    track.once("fade", () => track.stop());
    this.currentMusicId = null;
  }

  playSfx(id: string, opts?: { volume?: number }): void {
    const sound = this.sfx.get(id);
    if (!sound) return;
    sound.volume((opts?.volume ?? 1) * this.effectiveSfxVolume());
    sound.play();
  }

  setMasterVolume(v: number): void {
    this.masterVolume = Math.max(0, Math.min(1, v));
    this.applyVolumes();
  }

  setMusicVolume(v: number): void {
    this.musicVolume = Math.max(0, Math.min(1, v));
    this.applyVolumes();
  }

  setSfxVolume(v: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, v));
  }

  pauseAll(): void {
    for (const m of this.music.values()) m.pause();
  }

  resumeAll(): void {
    if (this.currentMusicId) {
      const track = this.music.get(this.currentMusicId);
      track?.play();
    }
  }

  private effectiveMusicVolume(): number {
    return this.masterVolume * this.musicVolume;
  }

  private effectiveSfxVolume(): number {
    return this.masterVolume * this.sfxVolume;
  }

  private applyVolumes(): void {
    for (const m of this.music.values()) {
      m.volume(this.effectiveMusicVolume());
    }
  }

  dispose(): void {
    for (const m of this.music.values()) m.unload();
    for (const s of this.sfx.values()) s.unload();
    this.music.clear();
    this.sfx.clear();
    this.currentMusicId = null;
  }
}
